import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { computePosition, flip, shift } from '@floating-ui/react';

import './toolbar-tooltip.scss';

export const TOOLBAR_TOOLTIP_CONTEXT = 'toolbar-tooltip';
export const TOOLBAR_POPPER_CONTEXT = 'toolbar-popper';

export const ToolbarTooltipContext = createContext(TOOLBAR_TOOLTIP_CONTEXT);
export const ToolbarPopperContext = createContext(TOOLBAR_POPPER_CONTEXT);

const TooltipProvider = ({ children }) => {
  const [currentTitle, setCurrentTitle] = useState('test');
  return (
    <ToolbarTooltipContext.Provider
      value={{
        currentTitle,
        setCurrentTitle,
      }}
    >
      {children}
    </ToolbarTooltipContext.Provider>
  );
};

const PopperProvider = ({ children }) => {
  const [anchor, setAnchor] = useState();
  return (
    <ToolbarPopperContext.Provider
      value={{
        anchor,
        setAnchor,
      }}
    >
      {children}
    </ToolbarPopperContext.Provider>
  );
};

export const ToolbarTooltipProvider = ({ children }) => {

  return (
    <PopperProvider>
      <TooltipProvider>
        {children}
        <ToolbarTooltip />
      </TooltipProvider>
    </PopperProvider>
  );
};

export const ToolbarTooltip = ({ tooltipDelay = 1000 }) => {
  const { currentTitle } = useContext(ToolbarTooltipContext);
  const { anchor } = useContext(ToolbarPopperContext);
  const contentRef = useRef();
  const openTimerRef = useRef(0);

  const [showTooltip, setShowTooltip] = useState(false);

  const handleDelayedOpen = useCallback(() => {
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = window.setTimeout(() => {
      setShowTooltip(true);
    }, tooltipDelay);
  }, [showTooltip]);

  // Delayed show
  useEffect(() => {
    if (currentTitle && anchor) {
      handleDelayedOpen();
    } else {
      setShowTooltip(false);
      window.clearTimeout(openTimerRef.current);
    }
  }, [currentTitle, anchor]);

  useEffect(() => {
    if (contentRef.current && anchor) {
      const tooltip = contentRef.current;
      computePosition(anchor, tooltip, {
        placement: 'bottom',
        middleware: [flip(), shift()],
      }).then(({ x, y }) => {
        Object.assign(tooltip.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
  }
  }, [showTooltip, anchor]);

  if (!showTooltip || !currentTitle || !anchor) return null;

  return (
    <div
      ref={contentRef}
      className="toolbar-tooltip"
    >
      {currentTitle}
    </div>
  );
};

export const useTooltip = () => {
  const { currentTitle, setCurrentTitle } = useContext(ToolbarTooltipContext);
  const { anchor, setAnchor } = useContext(ToolbarPopperContext);
  return {
    anchor,
    currentTitle,
    setCurrentTitle,
    setAnchor,
  };
};
